import { buildAppUrl } from "./url";

const MARKER = "data-app-og";

/** Default <title> from index.html — restored when leaving a shareable page. */
export const DEFAULT_DOCUMENT_TITLE = "OBA Zahira College Mawanella";

let previousTitle: string | null = null;

function upsertMeta(attr: "property" | "name", key: string, content: string) {
  const sel = `meta[${attr}="${key}"][${MARKER}]`;
  let el = document.head.querySelector(sel) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLinkRelCanonical(href: string) {
  let el = document.head.querySelector(`link[rel="canonical"][${MARKER}]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(MARKER, "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function truncateForOg(text: string, max = 280): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** Use absolute https URL for og:image (required by WhatsApp / Facebook). */
function absoluteImageUrl(url: string | null): string | null {
  if (!url?.trim()) return null;
  const u = url.trim();
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("//")) return `https:${u}`;
  const path = u.startsWith("/") ? u : `/${u}`;
  return buildAppUrl(path);
}

function defaultOgImageUrl(): string {
  return buildAppUrl("/fav.jpeg");
}

export type SharePageMetaOptions = {
  title: string;
  description: string | null;
  imageUrl: string | null;
  canonicalPath: string;
  /** @default "article" */
  ogType?: "article" | "website";
};

/**
 * Open Graph + Twitter Card tags (client-side). Many crawlers do not run JS — see index.html comment.
 * `og:image` must be an **absolute** `https://` URL (Supabase Storage URLs qualify).
 */
export function setSharePageMeta(opts: SharePageMetaOptions): void {
  const { title, description, imageUrl, canonicalPath } = opts;
  const ogType = opts.ogType ?? "article";
  const canonical = buildAppUrl(canonicalPath);
  const desc = description ? truncateForOg(description) : truncateForOg(title);
  const image = absoluteImageUrl(imageUrl) ?? defaultOgImageUrl();

  if (previousTitle === null) previousTitle = document.title;
  document.title = title;

  document.head.querySelectorAll('meta[property="og:image"],meta[name="twitter:image"]').forEach((el) => {
    if (!el.hasAttribute(MARKER)) el.remove();
  });

  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", desc);
  upsertMeta("property", "og:type", ogType);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:image:secure_url", image);
  upsertMeta("property", "og:image:alt", title);
  upsertMeta("property", "og:site_name", DEFAULT_DOCUMENT_TITLE);

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", desc);
  upsertMeta("name", "twitter:image", image);

  upsertMeta("name", "description", desc);

  upsertLinkRelCanonical(canonical);
}

export function setNewsArticleMeta(opts: Omit<SharePageMetaOptions, "ogType">): void {
  setSharePageMeta({ ...opts, ogType: "article" });
}

export function setProjectShareMeta(opts: Omit<SharePageMetaOptions, "ogType">): void {
  setSharePageMeta({ ...opts, ogType: "article" });
}

export function clearSharePageMeta(): void {
  document.head.querySelectorAll(`[${MARKER}]`).forEach((n) => n.remove());
  if (previousTitle !== null) {
    document.title = previousTitle;
    previousTitle = null;
  }
}

/** Alias for clearSharePageMeta (news pages). */
export const clearNewsArticleMeta = clearSharePageMeta;
